import {
  type UserStatus,
  type UserAttribute
} from '@mzc-watchover/adminapp-appsync'

type EntryFamilyItem = {
  name: string
  nameKana: string
  phone: string
  postalCode: string
  country: string
}

export type EntryContractor = {
  name: string
  lastName: string
  firstName: string
  nameKana: string
  lastNameKana: string
  firstNameKana: string
  postalCode: string
  address: string
  phone: string
  email: string
}
export type EntryUserData = {
  partner: string
  partnerId: string
  product: string
  productId: string
  service: string
  name: string
  lastName: string
  firstName: string
  nameKana: string
  lastNameKana: string
  firstNameKana: string
  postalCode: string
  address: string
  place: string
  placeNumber: string
  phone: string
  entry: string
  families: EntryFamilyItem[]
  country: string
  contractor?: EntryContractor | undefined
  necessaryContractor: boolean
  necessaryEKYC: boolean
  email?: string
  necessaryEmail: boolean
  attributes?: Array<{ key: string; value: string }>
  agreement: boolean
  ekyc?: string
  sign?: string
  signedPdf?: string
  organization?: string
  date?: string
}
export type EntryPartner = {
  id: string
  partner: string
  product: string
  orderByUser: boolean
  selectCountry: boolean
  agreement: string
  attributes?: Array<{
    key: string
    placeholder: string
    validate: number
    description: string
    url: string
  }>
  noEKYC: boolean
  noFamilies: boolean
  noFooter: boolean
  shortUrl: string
}
export enum Emails {
  toContractorForBoth = 'toContractorForBoth',
  toContractorForSign = 'toContractorForSign',
  toContractorForEkyc = 'toContractorForEkyc',
  toUserForBoth = 'toUserForBoth',
  toUserForSign = 'toUserForSign',
  toUserForEkyc = 'toUserForEkyc',
  reportForEkyc = 'reportForEkyc',
  reportForSign = 'reportForSign'
}
export type BoxEvent = {
  type: string
  id: string
  created_at: string
  trigger: string
  webhook: {
    id: string
    type: string
  }
  created_by: {
    type: string
    id: string
    name: string
    login: string
  }
  source: {
    id: string
    type: string
    file_version: {
      type: string
      id: string
      sha1: string
    }
    sequence_id: string
    etag: string
    sha1: string
    name: string
    description: string
    size: 11050
    path_collection: {
      total_count: 5
      entries: Array<{
        type: string
        id: string
        sequence_id: null
        etag: null
        name: string
      }>
    }
    created_at: string
    modified_at: string
    trashed_at: string
    purged_at: string
    content_created_at: string
    content_modified_at: string
    created_by: {
      type: string
      id: string
      name: string
      login: string
    }
    modified_by: {
      type: string
      id: string
      name: string
      login: string
    }
    owned_by: {
      type: string
      id: string
      name: string
      login: string
    }
    shared_link: null
    parent: {
      type: string
      id: string
      sequence_id: string
      etag: string
      name: string
    }
    item_status: string
  }
  additional_info: {
    sign_request_id: string
    signer_emails: string[]
    external_id: string
  }
}
export type Frame = {
  time: string
  amount: number
}
export type ElectricityDaily = {
  spin: string
  date: string
  amounts: Frame[]
  sum: number
  maxTemp?: number | null
  minTemp?: number | null
  temp?: Record<string, number | null> | null | undefined
}
export type ElectricityDemandData = {
  amount: number | undefined
  date: string | undefined
  amounts?: Array<{ date: string; maxAmount: { time: string; amount: number } }>
}
export type ElectricityDemandMonthlyData = {
  date: string
  demands: Array<{
    spin: string
    date: string
    demands: number[]
    amount: number
  }>
}
export type ElectricityDemandDailyData = {
  date: string
  demands: Array<{
    spin: string
    demands: number[]
    amounts: number[]
  }>
}
export type ElectricityDailyTargetsData = Array<{
  targetKey: string
  targetValue: string
  data: Record<string, ElectricityDaily>
}>
export type ElectricityMonthlyTargetsData = Array<{
  targetKey: string
  targetValue: string
  data: Record<string, ElectricityMonthly>
}>
export type ElectricityYearlyTargetsData = Array<{
  targetKey: string
  targetValue: string
  data: Record<string, ElectricityYearly>
}>
export type ElectricityDailyData = {
  data: ElectricityDaily | null | undefined
  compareData: ElectricityDaily | null | undefined
  additionalData?: ElectricityDaily | null | undefined
  targetsData?:
    | Array<{
        targetKey: string
        targetValue: string
        data: Record<string, ElectricityDaily>
      }>
    | null
    | undefined
  demandData?: ElectricityDemandData | null | undefined
}
export type ElectricityAllDailyData = {
  data: Array<ElectricityDaily | undefined> | undefined
  compareData: Array<ElectricityDaily | undefined> | undefined
}
export type ElectricityWeeklyData = {
  data: ElectricityWeekly | null | undefined
  compareData: ElectricityWeekly | null | undefined
}
export type ElectricityAllWeeklyData = {
  data: Array<ElectricityWeekly | undefined> | undefined
  compareData: Array<ElectricityWeekly | undefined> | undefined
}
export type ElectricityWeekly = {
  spin: string
  amounts: Array<{
    date: string
    daily: number | null
  }>
  sum: number
}
export type ElectricityMonthly = {
  spin: string
  amounts: Array<{
    date: string
    daily: number | null
    halfHourly: number[]
  }>
  sum: number
  date?: string
  maxTemp?: number | null
  minTemp?: number | null
  temp?: Array<{ date: string; maxTemp: number | null; minTemp: number | null }>
}
export type ElectricityYearly = {
  spin: string
  amounts: Array<{
    date: string
    daily: number | null
  }>
  sum: number
  date?: string
  maxTemp?: number | null
  minTemp?: number | null
}
export type ElectricityMonthlyData = {
  data: ElectricityMonthly | null | undefined
  compareData: ElectricityMonthly | null | undefined
  target: ElectricityMonthly | null | undefined
  targetsData?:
    | Array<{
        targetKey: string
        targetValue: string
        data: Record<string, ElectricityMonthly>
      }>
    | null
    | undefined
  demandData?: ElectricityDemandData | null | undefined
}
export type ElectricityAllMonthlyData = {
  data: Array<ElectricityMonthly | undefined> | undefined
  compareData: Array<ElectricityMonthly | undefined> | undefined
}
export type ElectricityYearlyData = {
  data: ElectricityYearly | null | undefined
  compareData: ElectricityYearly | null | undefined
  targetsData?:
    | Array<{
        targetKey: string
        targetValue: string
        data: Record<string, ElectricityYearly>
      }>
    | null
    | undefined
  demandData?: ElectricityDemandData | null | undefined
}
export type ElectricityAllYearlyData = {
  data: Array<ElectricityYearly | undefined> | undefined
  compareData: Array<ElectricityYearly | undefined> | undefined
}
export type SolarAccount = {
  name: string
  email: string
  group: string[] | undefined
  status?: string
  command?: string
  area?: string
  office?: string
  organizations: string[]
}
export enum ElectricityChartDailyKind {
  lastWeek = 'lastWeek',
  lastYearWithMonthlyAverage = 'lastYearWithMonthlyAverage',
  lastYearWithYearlyAverage = 'lastYearWithYearlyAverage'
}
export enum ElectricityChartMonthlyKind {
  lastYear = 'lastYear',
  lastMonth = 'lastMonth',
  lastYearWithMonthlyAverage = 'lastYearWithMonthlyAverage'
}
export enum ElectricityChartYearlyKind {
  lastYear = 'lastYear'
}
export type SpotTarget = {
  user: {
    id: string
    spin: string
    name: string
    attributes: UserAttribute[]
  }
  daily: {
    date: string
    threshold: number
    compareTo:
      | ElectricityChartDailyKind
      | ElectricityChartMonthlyKind
      | ElectricityChartYearlyKind
  }
  monthly: {
    date: string
    threshold: number
    compareTo:
      | ElectricityChartDailyKind
      | ElectricityChartMonthlyKind
      | ElectricityChartYearlyKind
  }
  yearly: {
    date: string
    compareTo:
      | ElectricityChartDailyKind
      | ElectricityChartMonthlyKind
      | ElectricityChartYearlyKind
  }
  mode: string | null | undefined
  idKey: string
}
export type Mapping = {
  id: string
  name: string
  address: string
  kind: string
  spin: string
  code: string
  status: UserStatus
  managedBy: string
  ownedBy: string
  from: string
  classification?: string
  group?: string | null
  floorArea?: number | null
  tenant?: boolean | null
} & Record<string, string | number | boolean>
export type AnnounceItem = {
  date: string
  content: string
  limit?: string | null | undefined
  file?: string | null | undefined
  partner?: string | null | undefined
}
export type EditData = {
  user: Mapping
  amounts: Array<{
    date: string
    daily: number | null
  }>
}
export type Comment = {
  date: string
  dateTime: string
  name: string
  email: string
  action: string
  comment: string
  target: Mapping
  targetDate: string
  up: boolean
}
export type AnalyzeTableItem = {
  durations: number[][]
  targets: string[]
  targetString: string
  all: boolean
  peak: boolean
  action: string
  compare: number
}
